import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  useTheme,
  useMediaQuery,
  Portal
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  TableChart as TableChartIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { blobticZoomService, ZoomConfig, ZoomData, ZoomResult } from '../services/blobticZoomService';
import { useLanguage } from '../contexts/LanguageContext';

interface BlobticZoomPopupProps {
  open: boolean;
  zoomKey: string;
  dynamicParams?: Record<string, string>;
  onClose: () => void;
  onSelect: (selectedData: ZoomData) => void;
  title?: string;
  width?: number;
  height?: number;
}

export const BlobticZoomPopup: React.FC<BlobticZoomPopupProps> = ({
  open,
  zoomKey,
  dynamicParams = {},
  onClose,
  onSelect,
  title,
  width,
  height
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ZoomData[]>([]);
  const [filteredData, setFilteredData] = useState<ZoomData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [fields, setFields] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [config, setConfig] = useState<ZoomConfig | undefined>();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  // États pour le drag & resize
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({
    width: isMobile ? window.innerWidth : 800,
    height: isMobile ? window.innerHeight : 600
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Charger les données au montage et quand les paramètres changent
  useEffect(() => {
    let controller: AbortController | null = null;
    let timeoutId: NodeJS.Timeout;

    if (open && zoomKey) {
      // Debounce pour éviter la double requête en StrictMode ou lors de changements rapides
      timeoutId = setTimeout(() => {
        controller = new AbortController();
        loadZoomData(controller.signal);
      }, 50);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (controller) controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, zoomKey, JSON.stringify(dynamicParams)]);

  // Filtrer les données quand le terme de recherche change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
  }, [data, searchTerm]);

  const loadZoomData = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    setSelectedRow(null);

    try {
      // Récupérer la configuration du zoom
      const zoomConfig = blobticZoomService.getZoomConfig(zoomKey);
      setConfig(zoomConfig);

      // Parser les champs et labels
      const { fields: zoomFields, labels: zoomLabels } = blobticZoomService.parseZoomFields(zoomKey);

      // Filter hidden columns
      if (zoomConfig?.hiddenColumns) {
        const filteredIndices = zoomFields
          .map((field, index) => (zoomConfig.hiddenColumns!.includes(field) ? -1 : index))
          .filter(index => index !== -1);

        setFields(filteredIndices.map(index => zoomFields[index]));
        setLabels(filteredIndices.map(index => zoomLabels[index]));
      } else {
        setFields(zoomFields);
        setLabels(zoomLabels);
      }

      // Valider le zoom
      const validationErrors = blobticZoomService.validateZoom(zoomKey, dynamicParams);
      if (validationErrors.length > 0) {
        setError(validationErrors.join(', '));
        setLoading(false);
        return;
      }

      // Exécuter l'action de zoom
      const result: ZoomResult = await blobticZoomService.executeZoomAction(zoomKey, dynamicParams, signal);

      if (signal?.aborted) return;

      if (result.success) {
        setData(result.data);
        setFilteredData(result.data);
      } else {
        setError(result.error || t('zoom.error.loading'));
      }
    } catch (err) {
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : t('zoom.error.unknown'));
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (index: number) => {
    setSelectedRow(index);
  };

  const handleSelect = () => {
    if (selectedRow !== null && filteredData[selectedRow]) {
      onSelect(filteredData[selectedRow]);
      onClose();
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedRow(null);
    setError(null);
    setExportMenuAnchor(null);
    // Réinitialiser la position et la taille
    setPosition({ x: 0, y: 0 });
    setSize({
      width: isMobile ? window.innerWidth : 800,
      height: isMobile ? window.innerHeight : 600
    });
    onClose();
  };

  // Fonctions pour le drag & resize
  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragStop = (e: any, data: any) => {
    setIsDragging(false);
    setPosition({ x: data.x, y: data.y });
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  const handleResizeStop = (e: any, { size }: any) => {
    setIsResizing(false);
    setSize({ width: size.width, height: size.height });
  };

  const handleRefresh = () => {
    loadZoomData();
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = () => {
    if (filteredData.length === 0) return;

    // Créer l'en-tête CSV
    const headers = labels.join(',');

    // Créer les lignes de données
    const rows = filteredData.map(item =>
      fields.map(field => {
        const value = item[field] !== undefined ? String(item[field]) : '';
        // Échapper les virgules et guillemets
        return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(',')
    );

    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${zoomKey}_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleExportMenuClose();
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) return;

    // Créer le contenu Excel (format CSV avec extension .xlsx)
    const headers = labels.join('\t');
    const rows = filteredData.map(item =>
      fields.map(field => {
        const value = item[field] !== undefined ? String(item[field]) : '';
        return value;
      }).join('\t')
    );

    const excelContent = [headers, ...rows].join('\n');
    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${zoomKey}_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleExportMenuClose();
  };

  const getDialogTitle = () => {
    if (title) return t(title);
    if (config?.title) return t(config.title);
    return t('zoom.title.default', { defaultValue: 'Sélection', zoomKey });
  };

  if (!open) return null;

  return (
    <Portal>
      {/* Styles CSS pour les poignées de resize */}
      <style>
        {`
          .react-resizable-handle {
            position: absolute;
            width: 24px;
            height: 24px;
            background-repeat: no-repeat;
            background-origin: content-box;
            box-sizing: border-box;
            border-radius: 4px;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .react-resizable-handle::before {
            content: '';
            width: 12px;
            height: 12px;
            background: linear-gradient(135deg, transparent 50%, ${theme.palette.text.primary} 50%);
            border-radius: 2px;
            transform: rotate(45deg);
            opacity: 0.5;
          }
          .react-resizable-handle::after {
            content: '';
            position: absolute;
            bottom: 2px;
            right: 2px;
            width: 8px;
            height: 8px;
            background: ${theme.palette.text.primary};
            border-radius: 1px;
            opacity: 0.5;
          }
          .react-resizable-handle:hover {
            background-color: ${theme.palette.action.hover};
            transform: scale(1.1);
          }
          .react-resizable-handle:hover::before {
            background: linear-gradient(135deg, transparent 50%, ${theme.palette.primary.main} 50%);
            opacity: 1;
          }
          .react-resizable-handle:hover::after {
            background: ${theme.palette.primary.main};
            opacity: 1;
          }
          .react-resizable-handle-se {
            bottom: 0px;
            right: 0px;
            cursor: se-resize;
          }
        `}
      </style>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1400, // Increased z-index to be above Dialogs (1300)
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 0 : 2,
        }}
      >
        <Draggable
          handle=".drag-handle"
          position={position}
          onStart={handleDragStart}
          onStop={handleDragStop}
          bounds="parent" // This bounds refers to the fixed Box overlay, which is correct
          disabled={isMobile}
        >
          {/* ... Content ... */}

          <ResizableBox
            width={isMobile ? window.innerWidth : size.width}
            height={isMobile ? window.innerHeight : size.height}
            onResizeStart={handleResizeStart}
            onResizeStop={handleResizeStop}
            minConstraints={isMobile ? [window.innerWidth, window.innerHeight] : [400, 300]}
            maxConstraints={isMobile ? [window.innerWidth, window.innerHeight] : [window.innerWidth * 0.95, window.innerHeight * 0.95]}
            resizeHandles={isMobile ? [] : ['se']}
          >
            <Box
              ref={dialogRef}
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: 'background.paper',
                borderRadius: isMobile ? 0 : '12px',
                boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0,0,0,0.15), 0 8px 25px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                cursor: isDragging ? 'grabbing' : 'default',
                border: isMobile ? 'none' : '1px solid rgba(0,0,0,0.05)'
              }}
            >
              {/* Header moderne - Zone de drag */}
              <Box
                className="drag-handle"
                sx={{
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
                  color: 'white',
                  p: 1.5,
                  position: 'relative',
                  cursor: 'grab',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:active': {
                    cursor: 'grabbing'
                  },
                  '&:hover': {
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 50%, ${theme.palette.primary.light} 100%)`
                  }
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: error
                          ? 'linear-gradient(135deg, #f44336, #d32f2f)'
                          : 'linear-gradient(135deg, #4caf50, #66bb6a)',
                        boxShadow: error
                          ? '0 0 8px rgba(244, 67, 54, 0.5)'
                          : '0 0 8px rgba(76, 175, 80, 0.5)'
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
                      {getDialogTitle()}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: 'white',
                      borderRadius: '50%',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Contenu principal */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      m: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('zoom.error.loading')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {error}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('zoom.error.advice')}
                      </Typography>
                    </Box>
                  </Alert>
                )}

                {/* Barre d'outils moderne */}
                <Box sx={{ p: 2, pb: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Barre de recherche */}
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder={t('zoom.search.placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        flexGrow: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 12px rgba(26, 35, 126, 0.15)'
                          }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchTerm('')}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary' }
                              }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    {/* Boutons d'action */}
                    <Tooltip title={t('zoom.action.refresh')}>
                      <IconButton
                        onClick={handleRefresh}
                        disabled={loading}
                        sx={{
                          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                          color: 'white',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                          boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}4D`,
                          '&:hover': {
                            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            transform: 'translateY(-1px)',
                            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}66`
                          },
                          '&:disabled': {
                            backgroundColor: (theme) => theme.palette.action.disabledBackground,
                            color: (theme) => theme.palette.text.disabled,
                            boxShadow: 'none'
                          }
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('zoom.action.export')}>
                      <IconButton
                        onClick={handleExportMenuOpen}
                        disabled={filteredData.length === 0}
                        sx={{
                          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          color: 'white',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                          boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}4D`,
                          '&:hover': {
                            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                            transform: 'translateY(-1px)',
                            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}66`
                          },
                          '&:disabled': {
                            backgroundColor: (theme) => theme.palette.action.disabledBackground,
                            color: (theme) => theme.palette.text.disabled,
                            boxShadow: 'none'
                          }
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>

                    {/* Menu d'export */}
                    <Menu
                      anchorEl={exportMenuAnchor}
                      open={Boolean(exportMenuAnchor)}
                      onClose={handleExportMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem onClick={exportToCSV}>
                        <FileDownloadIcon sx={{ mr: 1 }} />
                        {t('zoom.action.exportCsv')}
                      </MenuItem>
                      <MenuItem onClick={exportToExcel}>
                        <TableChartIcon sx={{ mr: 1 }} />
                        {t('zoom.action.exportExcel')}
                      </MenuItem>
                    </Menu>
                  </Stack>
                </Box>

                {/* Informations de statut */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      icon={<InfoIcon />}
                      label={t('zoom.status.results', filteredData.length, data.length)}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : `${theme.palette.primary.main}0D`,
                        borderRadius: '6px',
                        '& .MuiChip-icon': { color: 'primary.main' },
                        '&:hover': {
                          backgroundColor: (theme) => `${theme.palette.primary.main}1A`
                        }
                      }}
                      variant="outlined"
                    />
                    {searchTerm && (
                      <Chip
                        icon={<FilterListIcon />}
                        label={t('zoom.status.filtered', searchTerm)}
                        size="small"
                        sx={{
                          color: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.main,
                          borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.main,
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : `${theme.palette.secondary.main}0D`,
                          borderRadius: '6px',
                          '& .MuiChip-icon': {
                            color: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.main
                          },
                          '& .MuiChip-deleteIcon': {
                            color: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.main,
                            '&:hover': {
                              color: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.secondary.dark
                            }
                          },
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : `${theme.palette.secondary.main}1A`
                          }
                        }}
                        variant="outlined"
                        onDelete={() => setSearchTerm('')}
                      />
                    )}
                  </Stack>
                </Box>

                {/* Tableau moderne */}
                <Box sx={{ px: 2, pb: 2, flex: 1, overflow: 'hidden' }}>
                  <TableContainer
                    component={Paper}
                    sx={{
                      height: '100%',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'auto',
                      backgroundColor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px'
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f5f5f5',
                        borderRadius: '4px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'linear-gradient(135deg, #1a237e, #0277bd)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0d47a1, #01579b)'
                        }
                      }
                    }}
                  >
                    {loading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {labels.map((label, index) => (
                              <TableCell
                                key={index}
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.primary.main,
                                  color: (theme) => theme.palette.primary.contrastText,
                                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.1em',
                                  py: 1.5
                                }}
                              >
                                {label}
                              </TableCell>
                            ))}
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.primary.main,
                                color: (theme) => theme.palette.primary.contrastText,
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                width: 50,
                                textAlign: 'center',
                                py: 1.5
                              }}
                            >
                              ✓
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredData.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={labels.length + 1} align="center" sx={{ py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {searchTerm ? t('zoom.empty.search') : t('zoom.empty.data')}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredData.map((row, rowIndex) => (
                              <TableRow
                                key={rowIndex}
                                onClick={() => handleRowClick(rowIndex)}
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor: selectedRow === rowIndex ? 'rgba(26, 35, 126, 0.1)' : 'inherit',
                                  '&:hover': {
                                    backgroundColor: selectedRow === rowIndex ? 'rgba(26, 35, 126, 0.15)' : 'rgba(0,0,0,0.02)'
                                  },
                                  transition: 'all 0.2s ease',
                                  '&:nth-of-type(even)': {
                                    backgroundColor: selectedRow === rowIndex ? 'rgba(26, 35, 126, 0.1)' : 'rgba(0,0,0,0.01)'
                                  }
                                }}
                              >
                                {fields.map((field, fieldIndex) => (
                                  <TableCell
                                    key={fieldIndex}
                                    sx={{
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      fontWeight: selectedRow === rowIndex ? 500 : 400
                                    }}
                                  >
                                    {row[field] !== undefined ? String(row[field]) : ''}
                                  </TableCell>
                                ))}
                                <TableCell
                                  sx={{
                                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                                    textAlign: 'center'
                                  }}
                                >
                                  {selectedRow === rowIndex && (
                                    <CheckIcon sx={{
                                      fontSize: 20,
                                      color: 'primary.main',
                                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                                    }} />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                </Box>
              </Box>

              {/* Footer moderne */}
              <Box
                sx={{
                  p: 1.5,
                  borderTop: '1px solid rgba(0,0,0,0.08)',
                  backgroundColor: 'rgba(26, 35, 126, 0.02)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('zoom.hint.select')}
                  </Typography>

                  <Stack direction="row" spacing={0.5}>
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      sx={{
                        borderRadius: '8px',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          backgroundColor: (theme) => `${theme.palette.primary.main}0D`, // 5% opacity
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      {t('zoom.action.cancel')}
                    </Button>
                    <Button
                      onClick={handleSelect}
                      variant="contained"
                      disabled={selectedRow === null}
                      sx={{
                        borderRadius: '8px',
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, // 20% opacity
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                          transform: 'translateY(-1px)',
                          boxShadow: (theme) => `0 6px 16px ${theme.palette.primary.main}4D` // 30% opacity
                        },
                        '&.Mui-disabled': {
                          background: (theme) => theme.palette.action.disabledBackground,
                          color: (theme) => theme.palette.text.disabled,
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {t('zoom.action.select')}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </ResizableBox>
        </Draggable>
      </Box>
    </Portal >
  );
};
